import React, { Component } from "react";
import { Utils } from "../../../common/utils";
import * as d3 from "d3";

export enum viewBys {
    ASSIGNEE = "assignee_name",
    DASHBOARD = "dashboard",
    CREATOR = "creator",
    COMMITTEE = "committee",
    LABELGROUP = "label_group",
    LABEL = "label"
}

export enum sorting {
    OFF = 0,
    DESC = 1,
    ASC = 2
}

export interface ViewByLabels {
    readonly assignee_name: string
    readonly dashboard: string
    readonly creator: string
    readonly committee: string
    readonly label_group: string
    readonly label: string
}

export const viewByLabels: ViewByLabels = {
    assignee_name: "Assignee",
    dashboard: "Dashboard",
    creator: "Creator",
    committee: "Committee",
    label_group: "Label Group",
    label: "Label"
};

export enum categories {
    GRAND_TOTAL = "grand-total",
    OPEN_LATE = "open-late",
    CLOSED_NOT_MET = "closed-not-met",
    OPEN_ON_TIME = "open-on-time",
    CLOSED_MET = "closed-met",
    NO_DATE = "no-date"
}

export interface CategoryLabels {
    readonly "grand-total": string,
    readonly "open-late": string,
    readonly "closed-not-met": string,
    readonly "open-on-time": string,
    readonly "closed-met": string,
    readonly "no-date": string
}

export const categoryLabels: CategoryLabels = {
    "grand-total": "Grand Total",
    "open-late": "Open & Late",
    "closed-not-met": "Closed & Unmet",
    "open-on-time": "Open & On Time",
    "closed-met": "Closed & Achieved",
    "no-date": "No Date Assigned",
};

export const categoryStatus: CategoryLabels = {
    "grand-total": "All",
    "open-late": "Open & Late",
    "closed-not-met": "Closed & Unmet",
    "open-on-time": "Open & On Time",
    "closed-met": "Closed & Achieved",
    "no-date": "No Date Assigned",
};

const DATEFORMAT = "MM/DD/YYYY";

export interface DateSliderProps {
    onChange?: ({}) => void
    minDate: Date,
    maxDate: Date
}

interface DateSliderState {
    dateStart: Date
    dateStop: Date
}

export class DateSlider extends Component<DateSliderProps, DateSliderState> {
    sliderRef!: SVGElement | null;

    constructor(props: DateSliderProps) {
        super(props);
        this.state = {
            dateStart: this.props.minDate,
            dateStop: this.props.maxDate,
        };
    }

    componentDidMount() {
        this.renderSlider();
    }

    componentDidUpdate(prevProps: Readonly<DateSliderProps>) {
        if (prevProps.minDate !== this.props.minDate || prevProps.maxDate !== this.props.maxDate) {
            this.renderSlider();
        }
    }

    static getDerivedStateFromProps(props: DateSliderProps, state: DateSliderState) {
        const nextState: Partial<DateSliderState> = {};

        nextState.dateStart = (state.dateStart < props.minDate) ? props.minDate : state.dateStart;
        nextState.dateStop = (state.dateStop > props.maxDate) ? props.maxDate: state.dateStop;

        return nextState;
    }

    renderSlider() {
        const width = 200;
        const marginLeft = 10;
        const marginRight = 10;
        const height = 30;
        const barHeight = 10;
        const handleSize = 7.5;

        // bar y start =
        const x = d3.scaleTime()
            .domain([d3.utcDay(this.props.minDate), d3.utcDay(this.props.maxDate)])
            .range([0, width - marginLeft - marginRight]);

        const svg = d3.select("svg.dateSlider");
        svg.selectAll("*").remove();

        svg.attr("width", width)
            // .attr("class", "silly class")
            .attr("height", height)
            .attr("viewBox", [0, 0, width, height]);

        svg.append("g")
            .attr("class", "dateSliderRangeBar")
            .attr("transform", `translate(${marginLeft}, ${height / 2 - barHeight / 2})`)
            .append("rect")
            .attr("x", 0)
            .attr("y", 0)
            .attr("height", barHeight)
            .attr("width", width - marginLeft - marginRight)
            .attr("fill", "#e5e5e5");

        const brush = d3.brushX()
            .extent([
                [0, 0],
                [width - marginRight - marginLeft, barHeight]
            ]).handleSize(handleSize)
            .on("brush", ({selection}) => {
                svg.dispatch("input");
                d3.select(".dateSliderContainer .dateStart").text(
                    Utils.formatDate(d3.utcDay.round(x.invert(selection[0])), DATEFORMAT)
                );
                d3.select(".dateSliderContainer .dateStop").text(
                    Utils.formatDate(d3.utcDay.round(x.invert(selection[1])), DATEFORMAT)
                );
            });

        svg.append("g")
            .attr("class", "dateSliderBrush")
            .attr("width", width - marginLeft - marginRight)
            .attr("height", barHeight)
            .attr("transform", `translate(${marginLeft}, ${height / 2 - barHeight / 2})`)
            .call(brush)
            .call(brush.move, [x(this.state.dateStart), x(this.state.dateStop)]);

        brush.on("end", ({selection}) => {
            const start = d3.utcDay.round(x.invert(selection[0]));
            const stop = d3.utcDay.round(x.invert(selection[1]));
            this.setState({dateStart: start, dateStop: stop});
            if (this.props.onChange) {
                this.props.onChange({start: start, stop: stop});
            }
        });
    }

    render() {
        return (
            <div className="col-auto">
                <table className="dateSliderContainer">
                    <tbody>
                    <tr>
                        <td colSpan={3} className="label">Due Date</td>
                    </tr>
                    <tr>
                        <td className="date dateStart">
                            {Utils.formatDate(this.state.dateStart, DATEFORMAT)}
                        </td>
                        <td><svg className="dateSlider" ref={ref => this.sliderRef = ref} /></td>
                        <td className="date dateStop">
                            {Utils.formatDate(this.state.dateStop, DATEFORMAT)}
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        );
    }
}
